// /**
//  * Fleet - list
//  */

// React
import React, { useEffect, useContext } from 'react';
import HeaderBuilder from '../../utils/headerBuilder';
// Context
import AuthContext from '../../authContext';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiPencilOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiCar,
  mdiTextBoxMultipleOutline,
  mdiClose,
  mdiCheck,
} from '@mdi/js';

// Helper
import { translate } from '../../utils/helpers';

// Type
import { Fleet, Source } from './interfaces';
import theme from '../../theme';
import { Link } from 'react-router-dom';

type Data = Fleet;
type Props = {
  data: any;
  sourcesList: Source[];
  preferredSourcesLoading: boolean;
  preferredSources: any;
  handleConfirmUpdate: (body: Fleet) => void;
  handleEditPage: (id: number) => void;
  handleOpenDetailPage: (id: number) => void;
  updateVehicleSource: (payload: { id: number; body: number[] }) => void;
};

type Ord = 'asc' | 'desc';
interface HeadCell {
  id?: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    body: {
      fontSize: 14,
    },
  })
)(TableCell);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
  })
);

// Const
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const headCells: HeadCell[] = [
  { id: 'name', label: 'نام ناوگان' },
  {
    id: 'id',
    label: 'شناسه ناوگان',
  },
  { id: 'type', label: 'نوع ناوگان' },
  {
    id: 'driverCount',
    label: 'تعداد راننده',
  },
];

const IconSort = (order: Ord) => {
  return (
    <Icon path={order === 'asc' ? mdiChevronDown : mdiChevronUp} size={1} />
  );
};

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell />
        {headCells.map((headCell, index) => {
          let orderStatus = orderBy === headCell.id ? order : 'asc';
          return (
            <StyledTableCell
              key={index}
              align="center"
              sortDirection={orderBy === headCell.id ? order : false}
              data-cy={headCell.id}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderStatus}
                onClick={headCell.id && createSortHandler(headCell.id)}
                IconComponent={() => IconSort(orderStatus)}
              >
                {headCell.label}
              </TableSortLabel>
            </StyledTableCell>
          );
        })}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);

  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const [dense] = React.useState(false);
  const [checkboxValue, setCheckboxValue] = React.useState([] as any);

  useEffect(() => {
    setCheckboxValue(props.preferredSources);
  }, [props.preferredSources]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const handleChange = async (event: React.ChangeEvent<{ value: unknown }>) => {
    await setCheckboxValue(event.target.value);
  };
  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <HeaderBuilder
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
            filterVal={props.filterVal}
            onFilterValue={(filtervalues: any) => {
              props.onFilterValue(filtervalues);
            }}
            head={props.head}
            removeFilterHandler={props.removeFilterHandler}
            haveAction={true}
            firstSpace={true}
            filterLabelTranslator={props.filterLabelTranslator}
          />

          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                const updateStatus =
                  row.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';

                return (
                  <TableRow key={index}>
                    <TableCell align="center">
                      {' '}
                      <span
                        className={classes.dot}
                        style={{
                          backgroundColor:
                            row.status === 'ACTIVE'
                              ? theme.palette.green.main
                              : theme.palette.error.main,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">{row.name}</TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.id}
                    </TableCell>
                    <TableCell align="center">
                      {translate(row.type, 'fleet')}
                    </TableCell>
                    <TableCell align="center">{row.driverCount}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label="details"
                        size="small"
                        title="جزئیات ناوگان"
                        data-cy="details"
                        onClick={() => props.handleOpenDetailPage(row.id)}
                      >
                        <Icon
                          path={mdiTextBoxMultipleOutline}
                          size={1}
                          color={theme.palette.green.main}
                        />
                      </IconButton>
                      <Link to={`/fleets/${row.id}/drivers`}>
                        <IconButton title="رانندگان" size="small">
                          <Icon path={mdiCar} size={1} />
                        </IconButton>
                      </Link>
                      <IconButton
                        title="ویرایش"
                        size="small"
                        onClick={() => props.handleEditPage(row.id)}
                      >
                        <Icon
                          color={theme.palette.blue.main}
                          path={mdiPencilOutline}
                          size={1}
                        />
                      </IconButton>
                      <IconButton
                        title={
                          row.status === 'ACTIVE'
                            ? 'غیر فعال کردن'
                            : 'فعال کردن'
                        }
                        size="small"
                        onClick={() =>
                          props.handleConfirmUpdate({
                            ...row,
                            status: updateStatus,
                          })
                        }
                      >
                        <Icon
                          color={
                            row.status === 'ACTIVE'
                              ? theme.palette.error.main
                              : theme.palette.green.main
                          }
                          path={row.status === 'ACTIVE' ? mdiClose : mdiCheck}
                          size={1}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
